'use client';

import { Suspense, useLayoutEffect, useRef, type ReactNode } from 'react';
import { usePathname } from 'next/navigation';
import { SlidersHorizontal, UserCog } from 'lucide-react';
import { PageOverlay } from '../ui/PageOverlay';
import { focusOverlaySurface } from '../ui/overlayStack';
import { useTranslation } from '../../lib/i18n';
import { pluginLucideIcon } from '../../lib/pluginIcons';
import { resolvePluginSettingsRoute } from '../../lib/pluginNav';
import { pluginListingForPath } from '../../lib/sameDocumentNavigation';
import { isPageOverlayRoute, PAGE_OVERLAY_ROUTES } from '../../lib/usePagePath';
import { usePluginUi } from '../../lib/queries';
import { PluginHostView } from '../../modules/plugin/PluginHostView';

/** The shell owns one frame across routed page changes; Next's parallel slot owns only core content.
 * Plugin discovery may replace its content too, but must never unmount the presentation around it. */
export function PageOverlayHost({ children }: { children?: ReactNode }) {
  const pathname = usePathname();
  const { t, locale } = useTranslation();
  const listing = usePluginUi(locale);
  const body = useRef<HTMLDivElement>(null);
  const previousPath = useRef(pathname);

  useLayoutEffect(() => {
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;
    // Replaced page links need a new anchor; a retained plugin deck keeps its focused control.
    const surface = body.current?.closest<HTMLElement>('[data-elowen-modal]');
    if (surface && !surface.contains(document.activeElement)) focusOverlaySurface(surface);
  }, [pathname]);

  if (!isPageOverlayRoute(pathname, listing.data)) return null;

  const coreRoute = PAGE_OVERLAY_ROUTES.find((route) => pathname === route || pathname?.startsWith(`${route}/`));
  const core = {
    '/settings': { title: t.page.settings, icon: SlidersHorizontal, testId: undefined },
    '/account': { title: t.account.title, icon: UserCog, testId: 'account-overlay' },
  };
  const entry = pluginListingForPath(pathname, listing.data);
  const rest = pathname?.split('/').filter(Boolean).slice(2) ?? [];
  let presentation = coreRoute ? core[coreRoute] : undefined;

  if (entry) {
    const { explicitlyRequestedSetting: explicitSetting, bareSoleSetting: soleSetting } =
      resolvePluginSettingsRoute(entry, rest);
    const setting = explicitSetting ?? soleSetting;
    const route = rest.join('/');
    const nav = setting ? undefined : entry.nav.find((item) => (item.route ?? '') === route) ?? entry.nav[0];
    // Internal settings redirect into Settings → Plugins; retain the advertised entry's title meanwhile.
    const item = (setting?.placement === 'pluginDetail' ? undefined : setting) ?? nav;
    presentation = {
      title: item?.label ?? entry.label ?? entry.name,
      icon: pluginLucideIcon(item?.icon),
      testId: 'plugin-page-overlay',
    };
  }
  if (!presentation) throw new Error('Page overlay route has no presentation');

  // Modal mounts its portal in an effect, so slot content first renders in that urgent update rather than in
  // the router's transition. On a first open its client chunk is still loading and suspends there; unbounded,
  // that reaches LoginGate's shell-wide boundary and blanks the shell. Here it only delays the frame instead.
  return (
    <Suspense fallback={null}>
      <PageOverlay title={presentation.title} icon={presentation.icon} frame="reading" data-testid={presentation.testId}>
        <div
          ref={body}
          data-page-overlay-slot={entry ? undefined : ''}
          data-testid={entry ? 'plugin-page-overlay-body' : undefined}
          className={entry
            ? 'flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain'
            : 'flex min-h-0 flex-1 flex-col'}
          // Plugin leaf content needs a bounded fallback scroller and the deck's named responsive container.
          style={entry ? { container: 'workspace-shell / inline-size' } : undefined}
        >
          {entry ? <PluginHostView presentation="overlay" route={{ plugin: entry.name, rest }} /> : children}
        </div>
      </PageOverlay>
    </Suspense>
  );
}
